import { Option } from 'gossamer-unified';

export type AbsenceReasonOptionType = Option & { result: string };

export const AbsenceReasonOptions = [
  { result: 'noSession', id: 'session-cancelled', label: 'Teaching session cancelled' },
  { result: 'noSession', id: 'no-activity-available', label: 'No clinical activity available' },
  { result: 'absent', id: 'sickness', label: 'Sickness' },
  { result: 'absent', id: 'personal-leave', label: 'Authorised Personal Leave (e.g medical appointment, bereavement)' },
  { result: 'absent', id: 'educational-leave', label: 'Authorised Educational Leave (e.g. conference)' },
  { result: 'absent', id: 'religious-leave', label: 'Authorised Religious Leave' },
  { result: 'absent', id: 'other', label: 'Other (e.g. strike day)' },
];

export const AttendanceResultOptions = [
  { id: 'attended', label: 'attended' },
  { id: 'absent', label: 'absent' },
  { id: 'noSession', label: 'no session took place' },
  { id: 'notTracked', label: 'not tracked', active: false },
];

export const EventTypeOptions = [
  { id: 'clinic', label: 'Clinic' },
  { id: 'course', label: 'Course' },
  { id: 'conference', label: 'Conference' },
  { id: 'seminar', label: 'Seminar' },
  { id: 'lecture', label: 'Lecture' },
  { id: 'webinar', label: 'Webinar' },
  { id: 'on-demand', label: 'On-Demand', active: false }, // prevent new On-Demand events being created until further notice
  { id: 'simulation', label: 'Simulation' },
  { id: 'teaching-training', label: 'Teaching / Training' },
  { id: 'theatre', label: 'Theatre' },
  { id: 'ward-round-ward-work', label: 'Ward round / Ward work' },
];

export const EmploymentGradeOptions = [
  { id: '0', label: 'All grades' },
  { id: 'na', label: 'Not applicable' },
  { id: 'f1', label: 'Foundation Year 1' },
  { id: 'f2', label: 'Foundation Year 2' },
  { id: 'ct1', label: 'Core Trainee 1' },
  { id: 'ct2', label: 'Core Trainee 2' },
  { id: 'st3', label: 'Specialist Training (ST3+)' },
  { id: 'registrar', label: 'Registrar' },
  { id: 'consultant', label: 'Consultant' },
  { id: 'staff-grade-doctor', label: 'Staff Grade Doctor' },
  { id: 'clinical-fellow', label: 'Clinical Fellow' },
  { id: 'general-practitioner', label: 'General Practitioner (GP)' },
];

export const EventFlowOptions = [
  {
    id: 'none',
    label: 'none - no attendance tracking, feedback or certificate',
  },
  {
    id: 'attendanceOnly',
    label: 'attendance only - track attendance, but not feedback or certificates',
  },
  {
    id: 'noAttendance',
    label: 'no attendance - capture feedback and issue certificate but do not track attendance',
  },
  {
    id: 'full',
    label: 'full - track attendance, capture feedback and issue certificate',
  },
];

export const SpecialtyGroupOptions = [
  { id: 'medical', label: 'Medical Specialties' },
  { id: 'surgical', label: 'Surgical Specialties' },
  { id: 'anaesthesia_intensive_care', label: 'Anaesthesia and Intensive Care' },
  { id: 'primary_care', label: 'Primary Care Specialties' },
  { id: 'paediatrics', label: 'Paediatrics' },
  { id: 'obstetrics_gynaecology', label: 'Obstetrics and Gynaecology' },
  { id: 'psychiatry', label: 'Psychiatry' },
  { id: 'pathology', label: 'Pathology' },
  { id: 'radiology_imaging', label: 'Radiology and Imaging' },
  { id: 'career_development', label: 'Career Development' },
  { id: 'other', label: 'Other' },
];

export const StatusOptions = [
  { id: 'ACTIVE', label: 'active' },
  { id: 'INACTIVE', label: 'inactive' },
];

export const VisibilityLimitOptions = [
  { id: 'public', label: 'public - anyone can book, even outside the institution' },
  { id: 'private', label: 'private to members - members of the institution can book' },
  { id: 'private-group', label: 'private-group - only group members can view and book (no auto-booking)' },
  { id: 'closed', label: 'closed - auto-booked to group / bookings can only be made by administrators' },
  { id: 'cancelled', label: 'cancelled event', active: false },
];

export const VisibilityLimitOptionsNonEnterprise = VisibilityLimitOptions.map((option) => {
  if (['private-group', 'closed'].includes(option.id)) {
    return { ...option, active: false };
  }
  return option;
});

export const YearGroupOptions = [
  { id: 'all_years', label: 'All years' },
  { id: 'na', label: 'Not applicable' },
  { id: 'foundation', label: 'Foundation' },
  { id: 'intercalation', label: 'Intercalation' },
  { id: 'year_1', label: 'Year 1' },
  { id: 'year_2', label: 'Year 2' },
  { id: 'year_3', label: 'Year 3' },
  { id: 'year_4', label: 'Year 4' },
  { id: 'year_5', label: 'Year 5' },
  { id: 'year_6', label: 'Year 6' },
];

export const YesNoOptions = [
  { id: 'yes', label: 'yes' },
  { id: 'no', label: 'no' },
];

export const AttendanceCaptureTimeCutoffOptions = [
  { id: 'none', label: 'none', timeCutoffMinutes: null },
  { id: 'end-of-event', label: 'end of event', timeCutoffMinutes: 0 },
  { id: '15-mins-after-end-of-event', label: '15 minutes after the end of the event', timeCutoffMinutes: 15 },
  { id: '30-mins-after-end-of-event', label: '30 minutes after the end of the event', timeCutoffMinutes: 30 },
  { id: '1-hour-after-end-of-event', label: '1 hour after the end of the event', timeCutoffMinutes: 60 },
  { id: '24-hours-after-end-of-event', label: '24 hours after the end of the event', timeCutoffMinutes: 1440 },
  { id: '3-days-after-end-of-event', label: '3 days after the end of the event', timeCutoffMinutes: 4320 },
  { id: '7-days-after-end-of-event', label: '7 days after the end of the event', timeCutoffMinutes: 10080 },
];
